import { Box, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type Employee from '@src/Objects/Employee';
import { Clock, FolderOpen, TrendingUp } from 'lucide-react';
import React from 'react';

interface AvailabilityWidgetProps {
    employee: Employee;
}

function AvailabilityWidget({ employee }: AvailabilityWidgetProps) {
    const theme = useTheme();

    const availability = employee.availability?.availability;
    const workDayCount = employee.availability?.workDayCount;

    const MetricCard = ({
        title,
        value,
        color,
        icon,
    }: {
        title: string;
        value: string;
        color: string;
        icon: React.ReactNode;
    }) => (
        <Box
            sx={{
                p: 2,
                border: `1px solid ${alpha(color, 0.2)}`,
                borderRadius: 2,
                textAlign: 'center',
                bgcolor: alpha(color, 0.05),
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>{icon}</Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color, mb: 0.5 }}>
                {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {title}
            </Typography>
        </Box>
    );

    return (
        <Paper
            elevation={0}
            sx={{
                p: { lg: 4, md: 3, xs: 2 },
                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 2,
            }}
        >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Verfügbarkeit
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
                <MetricCard
                    title="Verfügbar"
                    value={`${availability}%`}
                    color={theme.palette.success.main}
                    icon={<Clock size={20} color={theme.palette.success.main} />}
                />
                <MetricCard
                    title="Projekte"
                    value={employee.projects?.filter((p) => p.active)?.length.toString() || '0'}
                    color={theme.palette.primary.main}
                    icon={<FolderOpen size={20} color={theme.palette.primary.main} />}
                />
                <MetricCard
                    title="Auslastung"
                    value={`${100 - availability}%`}
                    color={theme.palette.warning.main}
                    icon={<TrendingUp size={20} color={theme.palette.warning.main} />}
                />
            </Box>

            <Typography variant="body2" color="text.secondary">
                Nächste 30 Tage: {workDayCount} Arbeitstage
            </Typography>
        </Paper>
    );
}

export default AvailabilityWidget;
